import { api } from './api';
import { clearRegistrationUser } from 'utils/fns';

/**
 * Verifica se o usuario e executivo (so GE) na iniciativa.
 * Retorna true se o user esta em executivos[] mas nao em team[] nem e owner.
 */
export const isIniciativaExecutivoOnly = (
  iniciativaOriginal: any,
  userId: string,
): boolean => {
  if (!iniciativaOriginal || !userId) return false;
  const userIdLimpo = clearRegistrationUser(userId);

  const executivos: any[] = iniciativaOriginal?.executivos || [];
  const ehExecutivo = executivos.some(
    (ex) =>
      clearRegistrationUser(ex?._id || '') === userIdLimpo ||
      clearRegistrationUser(ex?.chave || '') === userIdLimpo,
  );
  if (!ehExecutivo) return false;

  const team: any[] = iniciativaOriginal?.team || [];
  const ehTime = team.some(
    (m) =>
      clearRegistrationUser(m?._id || '') === userIdLimpo ||
      clearRegistrationUser(m?.chave || '') === userIdLimpo,
  );
  if (ehTime) return false;

  const owner = clearRegistrationUser(iniciativaOriginal?.owner || '');
  if (owner && owner === userIdLimpo) return false;

  return true;
};

/**
 * Verifica se ja existe indicacao GE para uma iniciativa
 */
export const buscarIndicacaoExistente = async (
  idRealizacao: string,
): Promise<any | null> => {
  try {
    const { data } = await api.post('/find/indicacao_ge_by_realizacao', { idRealizacao });

    if (Array.isArray(data) && data.length > 0) {
      return data[0];
    }
    return null;
  } catch (error) {
    console.error('Erro ao buscar indicacao existente:', error);
    return null;
  }
};

/**
 * Salva indicacao do GE na collection indicacao_ge__c
 * Shape igual a insc_partic_premio__c + executivo: true
 */
export const salvarIndicacaoGE = async (
  idRealizacao: string,
  player: string,
  concorrentes_info: Array<{ player: string; nome: string; chave: string }>,
): Promise<any> => {
  // Lock otimista: verifica se outro GE ja indicou
  const existente = await buscarIndicacaoExistente(idRealizacao);
  if (existente) {
    throw new Error('Votação já realizada para esta iniciativa.');
  }

  const { data } = await api.post('/database/indicacao_ge__c', {
    player,
    id_realizacao: idRealizacao,
    dt_aceite: new Date(),
    concorrentes_info,
    rawPlayer: player,
    executivo: true,
  });

  return data;
};

/**
 * Busca ranking de votos dos participantes na etapa de inscricao
 * Reutiliza a query ranking_indicate de queries.ts
 */
export const buscarRankingParticipantes = async (
  idRealizacao: string,
): Promise<Array<{ player: string; name: string; registration: string; total: number }>> => {
  try {
    const { data } = await api.post('/find/ranking_indicate_by_realizacao', { idRealizacao });

    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    // A query retorna agrupado por id_realizacao, pegar o primeiro match
    const resultado = data[0];
    return resultado?.concorrentes_info || [];
  } catch (error) {
    console.error('Erro ao buscar ranking de participantes:', error);
    return [];
  }
};

export const PESO_VOTO_EMPREGADO = 0.1;
export const PESO_VOTO_GE = 2;

export interface RankingPontuacao {
  points: number;
  posicao: number;
}

/**
 * Monta ranking ponderado combinando votos de empregados (0.1 pts) e GE (2 pts)
 */
export const buildRankingPontuado = (
  rankingData: Array<{ player?: string; registration?: string; total: number }>,
  indicacaoGE?: { concorrentes_info?: Array<{ player?: string; chave?: string }> } | null,
): Map<string, RankingPontuacao> => {
  const pontos = new Map<string, number>();

  rankingData.forEach((r) => {
    const matricula = clearRegistrationUser(r.player || r.registration || '');
    if (!matricula) return;
    pontos.set(matricula, (pontos.get(matricula) || 0) + r.total * PESO_VOTO_EMPREGADO);
  });

  (indicacaoGE?.concorrentes_info || []).forEach((c) => {
    const matricula = clearRegistrationUser(c.player || c.chave || '');
    if (!matricula) return;
    pontos.set(matricula, (pontos.get(matricula) || 0) + PESO_VOTO_GE);
  });

  const ordenado = Array.from(pontos.entries()).sort((a, b) => b[1] - a[1]);
  const map = new Map<string, RankingPontuacao>();
  ordenado.forEach(([matricula, points], idx) => {
    map.set(matricula, { points, posicao: idx + 1 });
  });
  return map;
};

/**
 * Busca indicacoes existentes para multiplas iniciativas de uma vez
 */
export const buscarIndicacoesExistentes = async (
  idsRealizacao: string[],
): Promise<Map<string, any>> => {
  try {
    const { data } = await api.post('/find/indicacoes_ge_by_realizacoes', { idsRealizacao });

    const map = new Map<string, any>();
    if (Array.isArray(data)) {
      data.forEach((item: any) => {
        map.set(item.id_realizacao, item);
      });
    }
    return map;
  } catch (error) {
    console.error('Erro ao buscar indicacoes existentes:', error);
    return new Map();
  }
};
